import {
  TUTORIAL_FAKE_DOMAINS,
  TUTORIAL_LEGACY_CLIENT,
  TUTORIAL_REAL_NAME_REPLACEMENTS,
  fakeTutorialPhone,
  getTutorialContactPersona,
  getTutorialUserPersona,
  type TutorialFakePersona,
} from "../Constants/tutorialPersonas";
import { TUTORIAL_SCENARIO } from "../Constants/tutorialScenario";

const EMAIL_FIELD_KEYS = new Set([
  "email",
  "username",
  "contact_email",
  "contactEmail",
]);

const NAME_FIELD_KEYS = new Set([
  "name",
  "full_name",
  "fullName",
  "contactName",
  "display_name",
]);

const LINKEDIN_FIELD_KEYS = new Set([
  "linkedin_url",
  "linkedin_profile_url",
  "profile_url",
]);

const PHONE_FIELD_KEYS = new Set([
  "phone_number",
  "direct_phone_number",
  "mobile_phone_number",
  "corporate_phone_number",
  "phone",
]);

const LINK_FIELD_KEYS = new Set([
  "link_url",
  "file_url",
  "url",
]);

const TEXT_FIELD_KEYS = new Set([
  "message",
  "subject",
  "emailBody",
  "linkedInMessage",
  "summary",
  "synopsis",
  "short_summary",
  "context",
  "headline",
  "description",
  "notes",
]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sanitizeFreeText(value: string): string {
  let next = value;
  for (const [real, fake] of TUTORIAL_REAL_NAME_REPLACEMENTS) {
    next = next.split(real).join(fake);
  }
  next = next.split(TUTORIAL_LEGACY_CLIENT.name).join(TUTORIAL_SCENARIO.clientLabel);
  next = next.split(`${TUTORIAL_LEGACY_CLIENT.name}'s`).join(`${TUTORIAL_SCENARIO.clientShortLabel}'s`);
  next = next.replace(/\bAMC's\b/g, `${TUTORIAL_SCENARIO.clientShortLabel}'s`);
  next = next.replace(/\bAMC\b/g, TUTORIAL_SCENARIO.clientShortLabel);
  next = next.replace(/@accessmarketingcompany\.com/gi, `@${TUTORIAL_FAKE_DOMAINS.workspace}`);
  next = next.replace(/accessmarketingcompany\.com/gi, TUTORIAL_SCENARIO.clientDomain);
  next = next.replace(/\b[\w.+-]+@(?:zayo|cellhire|astound|altafiber)\.com\b/gi, (match) => {
    const local = match.split("@")[0]?.replace(/[^a-z0-9.+-]/gi, "") || "contact";
    return `${local}@${TUTORIAL_FAKE_DOMAINS.prospect}`;
  });
  next = next.replace(/https?:\/\/(?:www\.)?linkedin\.com\/in\/[^\s)\]"']+/gi, (match) => {
    const slug = match.split("/").pop() ?? "demo-profile";
    return `https://linkedin.com/in/demo-${slug.slice(0, 24)}`;
  });
  return next;
}

function applyUserPersona(target: Record<string, unknown>, persona: TutorialFakePersona): void {
  target.name = persona.firstName;
  target.full_name = persona.fullName;
  target.fullName = persona.fullName;
  target.email = persona.email;
  target.username = persona.email;
  target.linkedin_profile_url = persona.linkedinUrl;
  if ("linkedin_profile" in target && isPlainObject(target.linkedin_profile)) {
    target.linkedin_profile = {
      public_identifier: persona.linkedinUrl.split("/").pop(),
      first_name: persona.firstName,
      last_name: persona.lastName,
      full_name: persona.fullName,
      follower_count: 500,
      headline: "Sales professional",
      summary: "Demo profile for the App Guide.",
      country: "US",
      experiences: [],
      education: [],
      skills: [],
    };
  }
}

function applyContactPersona(target: Record<string, unknown>, persona: TutorialFakePersona): void {
  target.name = persona.fullName;
  target.full_name = persona.fullName;
  target.first_name = persona.firstName;
  target.last_name = persona.lastName;
  target.email = persona.email;
  target.linkedin_url = persona.linkedinUrl;
  if (target.linkedin_profile != null) {
    target.linkedin_profile = null;
  }
}

function looksLikeUserRecord(obj: Record<string, unknown>): boolean {
  return (
    typeof obj.id === "string" &&
    (obj.role != null || obj.username != null || (obj.full_name != null && obj.name != null))
  );
}

function looksLikeContactRecord(obj: Record<string, unknown>): boolean {
  return (
    typeof obj.id === "number" &&
    obj.name != null &&
    (obj.email != null || obj.title != null || obj.account_id != null)
  );
}

function sanitizeObject(obj: Record<string, unknown>): Record<string, unknown> {
  const next: Record<string, unknown> = { ...obj };

  if (looksLikeUserRecord(next)) {
    applyUserPersona(next, getTutorialUserPersona(String(next.id)));
  } else if (looksLikeContactRecord(next)) {
    applyContactPersona(next, getTutorialContactPersona(Number(next.id)));
  }

  for (const [key, value] of Object.entries(next)) {
    if (value == null) continue;

    if (typeof value === "string") {
      if (EMAIL_FIELD_KEYS.has(key)) {
        if (looksLikeUserRecord(next)) {
          next[key] = getTutorialUserPersona(String(next.id)).email;
        } else if (looksLikeContactRecord(next)) {
          next[key] = getTutorialContactPersona(Number(next.id)).email;
        } else {
          next[key] = `contact@${TUTORIAL_FAKE_DOMAINS.prospect}`;
        }
      } else if (NAME_FIELD_KEYS.has(key)) {
        if (looksLikeUserRecord(next)) {
          const persona = getTutorialUserPersona(String(next.id));
          next[key] = key === "name" ? persona.firstName : persona.fullName;
        } else if (looksLikeContactRecord(next)) {
          next[key] = getTutorialContactPersona(Number(next.id)).fullName;
        }
      } else if (LINKEDIN_FIELD_KEYS.has(key)) {
        if (looksLikeUserRecord(next)) {
          next[key] = getTutorialUserPersona(String(next.id)).linkedinUrl;
        } else if (looksLikeContactRecord(next)) {
          next[key] = getTutorialContactPersona(Number(next.id)).linkedinUrl;
        } else {
          next[key] = "https://linkedin.com/in/demo-profile";
        }
      } else if (LINK_FIELD_KEYS.has(key)) {
        next[key] = sanitizeFreeText(value);
      } else if (PHONE_FIELD_KEYS.has(key)) {
        const seed = looksLikeContactRecord(next)
          ? Number(next.id)
          : looksLikeUserRecord(next)
            ? String(next.id)
            : key;
        next[key] = fakeTutorialPhone(seed);
      } else if (TEXT_FIELD_KEYS.has(key) || value.includes("@") || value.includes("linkedin.com")) {
        next[key] = sanitizeFreeText(value);
      }
      continue;
    }

    if (Array.isArray(value)) {
      next[key] = value.map((entry) => {
        if (typeof entry === "string") return sanitizeFreeText(entry);
        return sanitizeTutorialData(entry);
      });
      continue;
    }

    if (isPlainObject(value)) {
      next[key] = sanitizeObject(value);
    }
  }

  return next;
}

/** Deep-clone fixture payloads and replace real people / contact info with demo personas. */
export function sanitizeTutorialData<T>(data: T): T {
  if (data == null) return data;
  if (Array.isArray(data)) {
    return data.map((entry) => sanitizeTutorialData(entry)) as T;
  }
  if (!isPlainObject(data)) {
    return data;
  }
  return sanitizeObject(data) as T;
}
